import React, { useState, useCallback, useRef } from "react";
import Cropper from "react-easy-crop";
import { toaster, Message } from "rsuite";
import authService from "../../../services/AuthService";
import "./ImageCropper.scss";
import {CropArea, CropPosition, Flip, ImageCropperProps, ImageSize} from "./ImageCropper.model";

const ImageCropper = ({ deviceId, onCropComplete, onImageUploaded = () => {} }: ImageCropperProps) => {
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState<CropPosition>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<CropArea | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [imageSize, setImageSize] = useState<ImageSize>({ width: 0, height: 0 });
  const containerRef = useRef<HTMLDivElement | null>(null);

  const ASPECT_RATIO = 100 / 80; // 1.25

  const onFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      const reader = new FileReader();

      reader.addEventListener("load", () => {
        const image = new Image();
        image.onload = () => {
          setImageSize({
            width: image.width,
            height: image.height
          });
          setImageSrc(reader.result as string);

          if (containerRef.current) {
            const containerRect = containerRef.current.getBoundingClientRect();
            const containerMinSide = Math.min(containerRect.width, containerRect.height);
            const targetSize = Math.min(100, 80);
            const initialZoom = (containerMinSide * 0.7) / targetSize;

            setZoom(Math.max(1, initialZoom));
          }
        };
        image.src = reader.result as string;
      });

      reader.readAsDataURL(file);
    }
  }, []);

  const onCropCompleteHandler = useCallback((_croppedArea: CropArea, pixels: CropArea) => {
    setCroppedAreaPixels(pixels);
  }, []);

  const getCroppedImgLocal = useCallback(async (src: string, pixelCrop: CropArea) => {
    const createImageLocal = (url: string) =>
      new Promise<HTMLImageElement>((resolve, reject) => {
        const image = new Image();
        image.addEventListener("load", () => resolve(image));
        image.addEventListener("error", (error) => reject(error));
        image.setAttribute("crossOrigin", "anonymous");
        image.src = url;
      });

    const image = await createImageLocal(src);
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    if (!ctx) {
      return null;
    }

    const outputWidth = 100;
    const outputHeight = 80;

    canvas.width = outputWidth;
    canvas.height = outputHeight;

    ctx.drawImage(
      image,
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
      0,
      0,
      outputWidth,
      outputHeight
    );

    return new Promise<Blob | null>((resolve) => {
      canvas.toBlob((blob) => {
        resolve(blob);
      }, "image/jpeg", 0.9);
    });
  }, []);

  const uploadToServer = useCallback(async (file: File) => {
    if (!file || !deviceId) return;

    setIsLoading(true);
    try {
      const formData = new FormData();
      formData.append("photo", file);

      console.log("Отправка FormData:", {
        hasPhoto: formData.has("photo"),
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type
      });

      const response = await fetch(`${window.location.origin}/api/ControlBlocks/${deviceId}/preview`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${await authService.isAuthenticated() ? await authService.getAccessToken() : ""}`
        },
        body: formData
      });

      if (response.ok) {
        toaster.push(
          <Message type="success" closable>
            Фото успешно загружено!
          </Message>,
          { placement: "topCenter", duration: 2000 }
        );

        if (onImageUploaded) {
          onImageUploaded();
        }

        if (onCropComplete) {
          onCropComplete();
        }
      } else {
        const errorText = await response.text();
        console.error("Ошибка сервера:", response.status, errorText);

        let errorMessage = "Ошибка загрузки";
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.errors?.photo?.[0] || errorData.title || errorMessage;
        } catch (e) {
          errorMessage = errorText || `HTTP error ${response.status}`;
        }

        throw new Error(errorMessage);
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : "Неизвестная ошибка";
      toaster.push(
        <Message type="error" closable>
          Ошибка при загрузке фото: {message}
        </Message>,
        { placement: "topCenter", duration: 2000 }
      );
    } finally {
      setIsLoading(false);
    }
  }, [deviceId, onImageUploaded, onCropComplete]);

  const handleCropAndUpload = useCallback(async () => {
    if (!imageSrc || !croppedAreaPixels || !deviceId) return;

    setIsLoading(true);
    try {
      const croppedImageBlob = await getCroppedImgLocal(imageSrc, croppedAreaPixels);

      if (!croppedImageBlob) {
        throw new Error("Не удалось получить обрезанное изображение");
      }

      const croppedFile = new File([croppedImageBlob], "cropped-image.jpg", {
        type: "image/jpeg",
        lastModified: Date.now()
      });

      await uploadToServer(croppedFile);

    } catch (error) {
      console.error("Ошибка при обрезке или загрузке:", error);
    } finally {
      setIsLoading(false);
    }
  }, [imageSrc, croppedAreaPixels, deviceId, getCroppedImgLocal, uploadToServer]);

  const handleReset = useCallback(() => {
    setImageSrc(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
    setImageSize({ width: 0, height: 0 });
  }, []);

  return (
    <div className="image-cropper p-1">
      {!imageSrc ? (
        <div className="upload-section">
          <input
            type="file"
            accept="image/*"
            onChange={onFileChange}
            className="file-input"
            id="image-upload"
          />
          <label htmlFor="image-upload" className="upload-button">
            Выберите изображение
          </label>
          <div className="upload-hint">
            Поддерживаемые форматы: JPG, PNG, GIF, WebP
          </div>
        </div>
      ) : (
        <div className="crop-container">
          <div className="crop-header">
            <h6>Выберите область 100×80 пикселей для обрезки</h6>
          </div>

          <div className="crop-wrapper" ref={containerRef}>
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={ASPECT_RATIO}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropCompleteHandler}
              cropShape="rect"
              showGrid={true}
              restrictPosition={true}
              minZoom={0.1}
              maxZoom={10}
            />
          </div>

          <div className="controls">
            <div className="zoom-control">
              <label>Масштаб:</label>
              <input
                type="range"
                min={0.1}
                max={5}
                step={0.1}
                value={zoom}
                onChange={(e) => setZoom(Number(e.target.value))}
                className="zoom-slider"
              />
              <span>{zoom.toFixed(1)}x</span>
            </div>
            <div className="buttons">
              <button
                onClick={handleReset}
                className="reset-button"
                disabled={isLoading}
              >
                Выбрать другое изображение
              </button>
              <button
                onClick={handleCropAndUpload}
                className="upload-button"
                disabled={isLoading || !croppedAreaPixels}
              >
                {isLoading ? "Загрузка..." : "Загрузить"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageCropper;

export const createImage = (url: string) =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    image.addEventListener("load", () => resolve(image));
    image.addEventListener("error", (error) => reject(error));
    image.setAttribute("crossOrigin", "anonymous");
    image.src = url;
  });

export function getRadianAngle(degreeValue: number) {
  return (degreeValue * Math.PI) / 180;
}

export function rotateSize(width: number, height: number, rotation: number) {
  const rotRad = getRadianAngle(rotation);

  return {
    width: Math.abs(Math.cos(rotRad) * width) + Math.abs(Math.sin(rotRad) * height),
    height: Math.abs(Math.sin(rotRad) * width) + Math.abs(Math.cos(rotRad) * height)
  };
}

export async function getCroppedImg(
  imageSrc: string,
  pixelCrop: CropArea,
  rotation = 0,
  flip: Flip = { horizontal: false, vertical: false }
) {
  const image = await createImage(imageSrc);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  if (!ctx) {
    return null;
  }

  const rotRad = getRadianAngle(rotation);

  const { width: bBoxWidth, height: bBoxHeight } = rotateSize(
    image.width,
    image.height,
    rotation
  );

  canvas.width = bBoxWidth;
  canvas.height = bBoxHeight;

  ctx.translate(bBoxWidth / 2, bBoxHeight / 2);
  ctx.rotate(rotRad);
  ctx.scale(flip.horizontal ? -1 : 1, flip.vertical ? -1 : 1);
  ctx.translate(-image.width / 2, -image.height / 2);

  ctx.drawImage(image, 0, 0);

  const data = ctx.getImageData(
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height
  );

  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;

  ctx.putImageData(data, 0, 0);

  return new Promise<string | null>((resolve) => {
    canvas.toBlob((file) => {
      if (!file) {
        resolve(null);
        return;
      }
      resolve(URL.createObjectURL(file));
    }, "image/jpeg");
  });
}
